export type IncidentClipTiming = {
  startMs: number | null;
  endMs: number | null;
  covered: boolean | null;
  gapMs: number | null;
  capturedAtMs: number | null;
  cloudReceivedAtMs: number | null;
};

type ParseTimingMs = (value: any) => number | null;

/**
 * Derive clip window + coverage for the incident drawer timing truth panel.
 *
 * When an evidence target is known, coverage is recomputed against that target
 * rather than trusting clip metadata `clip_event_covered` (late re-captures can
 * truthfully cover a rewritten local target while missing incident T0).
 */
export function computeIncidentClipTiming(args: {
  clipAttachmentMeta: any;
  selectedIncident: any;
  evidenceTargetTimeMs: number | null;
  parseTimingMs: ParseTimingMs;
  parseEvidenceFilenameMs: ParseTimingMs;
}): IncidentClipTiming {
  const {
    clipAttachmentMeta,
    selectedIncident,
    evidenceTargetTimeMs,
    parseTimingMs,
    parseEvidenceFilenameMs,
  } = args;
  const extra = clipAttachmentMeta?.metadata?.extra || {};
  const incidentTiming = selectedIncident?.timing || {};
  const envelopeTiming = selectedIncident?.envelope_metadata?.timing || {};
  const edgeTiming = selectedIncident?.edge_timing || {};
  const explicitGraceMs =
    [extra?.clip_event_grace_ms, clipAttachmentMeta?.metadata?.clip_event_grace_ms, incidentTiming?.clip_event_grace_ms, envelopeTiming?.clip_event_grace_ms, edgeTiming?.clip_event_grace_ms]
      .find((value) => value !== undefined && value !== null && Number.isFinite(Number(value))) !== undefined
      ? Math.max(
          0,
          Number(
            [extra?.clip_event_grace_ms, clipAttachmentMeta?.metadata?.clip_event_grace_ms, incidentTiming?.clip_event_grace_ms, envelopeTiming?.clip_event_grace_ms, edgeTiming?.clip_event_grace_ms]
              .find((value) => value !== undefined && value !== null && Number.isFinite(Number(value))),
          ),
        )
      : 750;
  const explicitStartMs =
    parseTimingMs(extra?.clip_start_ts_ms) ||
    parseTimingMs(clipAttachmentMeta?.metadata?.clip_start_ts_ms) ||
    parseTimingMs(incidentTiming?.clip_start_ts_ms) ||
    parseTimingMs(envelopeTiming?.clip_start_ts_ms) ||
    parseTimingMs(edgeTiming?.clip_start_ts_ms);
  const explicitEndMs =
    parseTimingMs(extra?.clip_end_ts_ms) ||
    parseTimingMs(clipAttachmentMeta?.metadata?.clip_end_ts_ms) ||
    parseTimingMs(incidentTiming?.clip_end_ts_ms) ||
    parseTimingMs(envelopeTiming?.clip_end_ts_ms) ||
    parseTimingMs(edgeTiming?.clip_end_ts_ms);
  const explicitCovered =
    (
      [extra?.clip_event_covered, clipAttachmentMeta?.metadata?.clip_event_covered, incidentTiming?.clip_event_covered, envelopeTiming?.clip_event_covered, edgeTiming?.clip_event_covered]
        .find((value) => typeof value === 'boolean') as boolean | undefined
    ) ?? null;
  const explicitGapMs =
    [extra?.clip_event_gap_ms, clipAttachmentMeta?.metadata?.clip_event_gap_ms, incidentTiming?.clip_event_gap_ms, envelopeTiming?.clip_event_gap_ms, edgeTiming?.clip_event_gap_ms]
      .find((value) => value !== undefined && value !== null && Number.isFinite(Number(value))) !== undefined
      ? Number(
          [extra?.clip_event_gap_ms, clipAttachmentMeta?.metadata?.clip_event_gap_ms, incidentTiming?.clip_event_gap_ms, envelopeTiming?.clip_event_gap_ms, edgeTiming?.clip_event_gap_ms]
            .find((value) => value !== undefined && value !== null && Number.isFinite(Number(value))),
        )
      : null;
  const capturedAtMs =
    parseTimingMs(clipAttachmentMeta?.metadata?.timestamp) ||
    parseTimingMs(clipAttachmentMeta?.metadata?.captured_at) ||
    parseEvidenceFilenameMs(clipAttachmentMeta?.filename) ||
    null;
  const cloudReceivedAtMs = parseTimingMs(clipAttachmentMeta?.uploaded_at) || null;
  // Prefer cloud-computed coverage on the selected attachment when present
  // (Package G: producer clip_event_covered is non-authoritative).
  const serverComputed =
    typeof clipAttachmentMeta?.metadata?.computed_covers_t0 === 'boolean'
      ? (clipAttachmentMeta.metadata.computed_covers_t0 as boolean)
      : typeof selectedIncident?.evidence_selection?.preferred_clip?.computed_covers_t0 === 'boolean' &&
          String(selectedIncident?.evidence_selection?.preferred_clip?.filename || '') ===
            String(clipAttachmentMeta?.filename || '')
        ? (selectedIncident.evidence_selection.preferred_clip.computed_covers_t0 as boolean)
        : null;

  if (explicitStartMs && explicitEndMs) {
    let covered: boolean | null = null;
    let gapMs: number | null = null;
    if (evidenceTargetTimeMs) {
      const inWindow =
        evidenceTargetTimeMs >= (explicitStartMs - explicitGraceMs) &&
        evidenceTargetTimeMs <= (explicitEndMs + explicitGraceMs);
      covered = inWindow;
      gapMs = inWindow
        ? 0
        : evidenceTargetTimeMs < explicitStartMs
          ? explicitStartMs - evidenceTargetTimeMs
          : evidenceTargetTimeMs - explicitEndMs;
    } else if (serverComputed !== null) {
      covered = serverComputed;
      gapMs =
        typeof clipAttachmentMeta?.metadata?.computed_coverage_gap_ms === 'number'
          ? Number(clipAttachmentMeta.metadata.computed_coverage_gap_ms)
          : explicitGapMs;
    } else if (explicitCovered !== null) {
      covered = explicitCovered;
      if (explicitGapMs !== null) {
        gapMs =
          covered === true && explicitGapMs <= explicitGraceMs ? 0 : explicitGapMs;
      }
      if (covered === false && explicitGapMs !== null && explicitGapMs <= explicitGraceMs) {
        covered = true;
        gapMs = 0;
      }
    }
    return { startMs: explicitStartMs, endMs: explicitEndMs, covered, gapMs, capturedAtMs, cloudReceivedAtMs };
  }
  const clipSeconds = Number(
    extra?.clip_seconds ||
      clipAttachmentMeta?.metadata?.clip_seconds ||
      incidentTiming?.clip_seconds ||
      envelopeTiming?.clip_seconds ||
      edgeTiming?.clip_seconds ||
      0,
  );
  const durationMs = Number.isFinite(clipSeconds) && clipSeconds > 0 ? Math.round(clipSeconds * 1000) : 0;
  const requestMs =
    parseTimingMs(extra?.capture_requested_at) ||
    parseTimingMs(clipAttachmentMeta?.metadata?.capture_requested_at) ||
    parseTimingMs(incidentTiming?.capture_requested_ts_ms) ||
    parseTimingMs(incidentTiming?.capture_requested_at) ||
    parseTimingMs(envelopeTiming?.capture_requested_ts_ms) ||
    parseTimingMs(envelopeTiming?.capture_requested_at) ||
    parseTimingMs(edgeTiming?.capture_requested_ts_ms) ||
    parseTimingMs(edgeTiming?.capture_requested_at) ||
    parseTimingMs(
      Number.isFinite(Number(extra?.capture_requested_epoch))
        ? Number(extra.capture_requested_epoch) * 1000
        : null,
    ) ||
    capturedAtMs;
  if (!requestMs || durationMs <= 0) {
    return { startMs: null, endMs: null, covered: null, gapMs: null, capturedAtMs, cloudReceivedAtMs };
  }
  const postMs = Number.isFinite(Number(extra?.clip_post_ms || clipAttachmentMeta?.metadata?.clip_post_ms || incidentTiming?.clip_post_ms || envelopeTiming?.clip_post_ms || edgeTiming?.clip_post_ms))
    ? Math.max(0, Number(extra?.clip_post_ms || clipAttachmentMeta?.metadata?.clip_post_ms || incidentTiming?.clip_post_ms || envelopeTiming?.clip_post_ms || edgeTiming?.clip_post_ms))
    : 0;
  const source = String(
    extra?.source ||
      clipAttachmentMeta?.metadata?.source ||
      incidentTiming?.clip_source ||
      envelopeTiming?.clip_source ||
      edgeTiming?.clip_source ||
      '',
  ).trim().toLowerCase();
  let startMs = requestMs + 500;
  let endMs = startMs + durationMs;
  if (source === 'review_segment_ring') {
    endMs = requestMs + postMs + 500;
    startMs = endMs - durationMs;
  } else if (source === 'mkv_recording' || source === 'mkv_recording_copy') {
    const triggered = Boolean(
      extra?.clip_triggered_recording ??
        clipAttachmentMeta?.metadata?.clip_triggered_recording ??
        incidentTiming?.clip_triggered_recording ??
        envelopeTiming?.clip_triggered_recording ??
        edgeTiming?.clip_triggered_recording,
    );
    endMs = requestMs + (triggered ? postMs : 0);
    startMs = endMs - durationMs;
  }
  const covered = evidenceTargetTimeMs
    ? evidenceTargetTimeMs >= (startMs - explicitGraceMs) &&
      evidenceTargetTimeMs <= (endMs + explicitGraceMs)
    : null;
  const gapMs =
    evidenceTargetTimeMs && covered === false
      ? evidenceTargetTimeMs < startMs
        ? startMs - evidenceTargetTimeMs
        : evidenceTargetTimeMs - endMs
      : null;
  return { startMs, endMs, covered, gapMs, capturedAtMs, cloudReceivedAtMs };
}
